/**
 * klubb-cart-badge — keeps the header cart-count badge current across AJAX
 * cart changes (stock Horizon quick-add on PLPs, the cart drawer, any future
 * fetch-based add). The Liquid-rendered badge in klubb-header.liquid is the
 * server baseline; this module only refreshes it between navigations.
 *
 * Listens for the stock theme's `cart:update` document event (the name behind
 * ThemeEvents.cartUpdate in events.js — duplicated here so klubb assets don't
 * import stock modules). The event payload's itemCount is ambiguous by source
 * (a DELTA from product forms, an ABSOLUTE count elsewhere — see
 * cart-icon.js:74), so instead of interpreting it we re-fetch /cart.js and
 * count its lines ourselves.
 */

// Units the shopper picked, without print charges: a Trykk line
// (`_print_for`) is part of its garment, not an item of its own (19.10.2026).
// Mirrors snippets/klubb-cart-count.liquid.
export function countItems(cart) {
  if (!cart || !Array.isArray(cart.items)) return 0;
  return cart.items.reduce(function (sum, item) {
    const props = item.properties || {};
    return props._print_for ? sum : sum + (item.quantity || 0);
  }, 0);
}

export function formatCount(count) {
  return count > 99 ? '99+' : String(count);
}

// Create/update/remove the badge inside the header cart link and keep the
// link's aria-label in sync ("Handlekurv (3)"). Returns false when the klubb
// header isn't on the page (safe no-op on stock-header contexts).
export function renderCount(doc, count) {
  const link = doc.querySelector('[data-klubb-cart-link]');
  if (!link) return false;
  const base = link.getAttribute('data-klubb-cart-label') || '';
  let badge = link.querySelector('.klubb-header__cart-count');
  if (count > 0) {
    if (!badge) {
      badge = doc.createElement('span');
      badge.className = 'klubb-header__cart-count';
      badge.setAttribute('aria-hidden', 'true');
      link.appendChild(badge);
    }
    badge.textContent = formatCount(count);
    if (base) link.setAttribute('aria-label', base + ' (' + count + ')');
  } else {
    if (badge) badge.remove();
    if (base) link.setAttribute('aria-label', base);
  }
  return true;
}

/* ============================================================
 * Browser wiring. Debounced so bursts of cart events (drawer qty spam)
 * collapse into one /cart.js read; errors leave the server-rendered badge
 * untouched rather than guessing.
 * ============================================================ */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  let timer = null;
  function refresh() {
    clearTimeout(timer);
    timer = setTimeout(function () {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { renderCount(document, countItems(cart)); })
        .catch(function () { /* keep the server-rendered badge */ });
    }, 150);
  }

  // Fired by stock quick-add, product forms, and the cart drawer after
  // successful cart mutations (ThemeEvents.cartUpdate).
  document.addEventListener('cart:update', refresh);
  // Back/forward-cache restores show a stale DOM if the cart changed since.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) refresh();
  });
})();
